import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// ─── Types ───

export interface PinnedRoute {
  key: string; // stable id e.g. 'queue', 'appt:list', 'services'
  label: string;
  route: string;
  icon: string;
  scope: 'clinic' | 'patient';
}

export interface PinnedRoutesContextValue {
  pinnedRoutes: PinnedRoute[];
  isPinned: (key: string) => boolean;
  togglePinned: (key: string) => void;
}

// ─── All pinnable clinic items (registry) ───

export const PINNABLE_ITEMS: PinnedRoute[] = [
  // Queue
  { key: 'queue', label: 'Queue', route: '/clinic/queue', icon: 'list-ordered', scope: 'clinic' },
  // Patients
  {
    key: 'patients:today',
    label: "Today's Patients",
    route: '/patients/today',
    icon: 'calendar',
    scope: 'clinic',
  },
  { key: 'patients:all', label: 'All Patients', route: '/patients', icon: 'list', scope: 'clinic' },
  {
    key: 'patients:favorites',
    label: 'Favorites',
    route: '/patients/favorites',
    icon: 'star',
    scope: 'clinic',
  },
  {
    key: 'patient:create',
    label: 'Create Patient',
    route: '/patient/new',
    icon: 'user-plus',
    scope: 'clinic',
  },
  // Appointment views
  {
    key: 'appt:month',
    label: 'Month View',
    route: '/appointments/month',
    icon: 'calendar',
    scope: 'clinic',
  },
  {
    key: 'appt:week',
    label: 'Week View',
    route: '/appointments/week',
    icon: 'calendar',
    scope: 'clinic',
  },
  {
    key: 'appt:day',
    label: 'Day View',
    route: '/appointments/day',
    icon: 'calendar',
    scope: 'clinic',
  },
  {
    key: 'appt:list',
    label: 'List View',
    route: '/appointments/list',
    icon: 'list',
    scope: 'clinic',
  },
  // Appointment management
  {
    key: 'services',
    label: 'Services',
    route: '/appointments/services',
    icon: 'clipboard',
    scope: 'clinic',
  },
  {
    key: 'providers',
    label: 'Providers',
    route: '/appointments/providers',
    icon: 'stethoscope',
    scope: 'clinic',
  },
  // Settings
  {
    key: 'settings:calendar',
    label: 'Calendar Settings',
    route: '/appointments/settings/calendar',
    icon: 'calendar',
    scope: 'clinic',
  },
  {
    key: 'settings:categories',
    label: 'Categories',
    route: '/appointments/settings/categories',
    icon: 'clipboard',
    scope: 'clinic',
  },
  {
    key: 'settings:online-booking',
    label: 'Online Booking',
    route: '/appointments/settings/online-booking',
    icon: 'credit-card',
    scope: 'clinic',
  },
  {
    key: 'settings:front-desk',
    label: 'Front Desk Booking',
    route: '/appointments/settings/front-desk-booking',
    icon: 'clipboard',
    scope: 'clinic',
  },
  {
    key: 'settings:patient-profile',
    label: 'Patient Profile',
    route: '/clinic/patient-profile',
    icon: 'clipboard',
    scope: 'clinic',
  },
  {
    key: 'settings:patient-form',
    label: 'Patient Form',
    route: '/clinic/patient-form',
    icon: 'edit',
    scope: 'clinic',
  },
  {
    key: 'settings:vitals',
    label: 'Vitals',
    route: '/clinic/vitals-definitions',
    icon: 'activity',
    scope: 'clinic',
  },
  {
    key: 'settings:intake-form',
    label: 'Intake Form',
    route: '/clinic/intake-form',
    icon: 'clipboard',
    scope: 'clinic',
  },
  {
    key: 'settings:forms',
    label: 'Forms',
    route: '/clinic/forms',
    icon: 'file-text',
    scope: 'clinic',
  },
];

// ─── Role-based defaults ───

type RoleKey = 'admin' | 'editor' | 'viewer' | 'default';

const ROLE_DEFAULTS: Record<RoleKey, string[]> = {
  admin: [],
  editor: [],
  viewer: [],
  default: [],
};

/** Map from route path → pin key for lookup in sidebar components. */
export const ROUTE_TO_PIN_KEY = new Map(PINNABLE_ITEMS.map((item) => [item.route, item.key]));

// ─── localStorage helpers ───

function storageKey(orgKey: string, userId: string) {
  return `helios:pinnedRoutes:v2:${orgKey}:${userId}`;
}

function readPins(key: string): string[] | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writePins(key: string, keys: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(keys));
  } catch {
    // Private browsing / storage full — ignore
  }
}

// ─── Context ───

const PinnedRoutesContext = createContext<PinnedRoutesContextValue>({
  pinnedRoutes: [],
  isPinned: () => false,
  togglePinned: () => {},
});

export const PinnedRoutesProvider = PinnedRoutesContext.Provider;

/**
 * Initializes pinned routes state. Call once at the app shell level,
 * then pass the returned value to <PinnedRoutesProvider value={...}>.
 */
export function usePinnedRoutesInit(params: {
  orgKey: string;
  userId: string;
  role?: string;
}): PinnedRoutesContextValue {
  const { orgKey, userId, role } = params;
  const sKey = storageKey(orgKey, userId);

  const [pinnedKeys, setPinnedKeys] = useState<string[]>(() => {
    return readPins(sKey) ?? [];
  });
  const [seeded, setSeeded] = useState(false);

  // Seed defaults on first run (only if localStorage has no entry at all)
  useEffect(() => {
    if (seeded) return;
    setSeeded(true);
    const existing = readPins(sKey);
    if (existing !== null) return;

    const roleKey: RoleKey =
      role === 'admin' || role === 'editor' || role === 'viewer' ? role : 'default';
    const defaults = ROLE_DEFAULTS[roleKey];
    writePins(sKey, defaults);
    setPinnedKeys(defaults);
  }, [sKey, role, seeded]);

  const pinnedRoutes = useMemo(() => {
    const map = new Map(PINNABLE_ITEMS.map((item) => [item.key, item]));
    return pinnedKeys.map((k) => map.get(k)).filter(Boolean) as PinnedRoute[];
  }, [pinnedKeys]);

  const isPinned = useCallback((key: string) => pinnedKeys.includes(key), [pinnedKeys]);

  const togglePinned = useCallback(
    (key: string) => {
      setPinnedKeys((prev) => {
        const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
        writePins(sKey, next);
        return next;
      });
    },
    [sKey],
  );

  return { pinnedRoutes, isPinned, togglePinned };
}

/**
 * Consume pinned routes from context. Use in TopBar, sidebars, etc.
 */
export function usePinnedRoutes(): PinnedRoutesContextValue {
  return useContext(PinnedRoutesContext);
}
